import { Button, Grid, Tooltip } from '@mui/material';
import { ConfigContext } from 'components/ConfigGuard';
import { UserContext } from 'components/UserGuard';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PaymentService from 'services/api/PaymentService';
import SubscriptionService from 'services/api/SubscriptionService';
import SiteModule from 'store/enums/SiteModule';
import { OrderPayment } from 'store/types/Payment';
import Badge from 'store/types/Badge';
import { QuickLinkConfigItem } from 'store/types/SiteConfig';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import useRequest from 'hooks/useRequest';
import HomeAnnouncementsCard from './HomeAnnouncementsCard';
import HomeCreditsSection from './HomeCreditsSection';
import HomeMembershipCard from './HomeMembershipCard';
import HomeNccerNoticeCard from './HomeNccerNoticeCard';
import HomeOpenOrdersSection from './HomeOpenOrdersSection';
import HomeOrdersCard from './HomeOrdersCard';
import HomeNavigationCard from './HomeNavigationCard/HomeNavigationCard';
import HomeBadgesSection from './HomeBadgesSection';
import DonationTermsModal from 'components/donation/DonationPaymentPageView/DonationTermsModal';
import { tenantConfig } from 'config';
import { hasMemberType, isModuleAvailable } from 'util/User';

import styles from './HomePage.module.scss';

interface CardsConfigItem {
  component: React.ReactNode;
  module?: SiteModule;
  key: string;
  fullWidth?: boolean;
}

const HomePage: React.FunctionComponent = () => {
  const { id = '', memberTypeName, nccerCardNumber, functions, userPermissions } = useContext(UserContext);
  const { addNccerCardNumberUrl = '' } = tenantConfig;
  const {
    isNccerTheme,
    isCompassTheme,
    isMemberplexTheme,
    isDemoTheme,
    isSmacnaTheme,
    isWcaTheme,
    isUsAseanTheme,
    isBayViewTheme,
    quickLinks,
    enabledModules,
    modulesSettings: {
      home: { enableNavigationSection, enableNotes, navigationLinks },
      nccer: { enableCardNumber },
    },
  } = useContext(ConfigContext);
  const ordersRequestData = useRequest<OrderPayment[]>(id && !isNccerTheme ? PaymentService.getOrdersList : undefined);
  const badgeRequestData = useRequest<Badge[]>(id ? SubscriptionService.getBadges : undefined);
  const [termsAndConditionsModalOpen, setTermsAndConditionsModalOpen] = useState<boolean>(false);
  const quickLinksConfig: QuickLinkConfigItem[] = useMemo(
    () =>
      isNccerTheme && !nccerCardNumber
        ? quickLinks.filter(({ label }) => label !== 'Registry' && label !== 'Module Testing')
        : quickLinks,
    [isNccerTheme, nccerCardNumber, quickLinks]
  );
  const navigationCards: React.ReactNode[] = useMemo(
    () =>
      (navigationLinks || [])
        .filter(({ type }) => isModuleAvailable(type, functions, userPermissions))
        .map(
          ({ title, icon, path, memberTypes }) =>
            hasMemberType(memberTypeName, memberTypes) && (
              <Grid {...defaultGridItemProps} sm={true} key={`navigation-card-${title}`}>
                <HomeNavigationCard title={title} icon={icon} path={path} />
              </Grid>
            )
        ),
    [functions, memberTypeName, navigationLinks, userPermissions]
  );

  const cardsConfig: CardsConfigItem[] = useMemo(() => {
    const cards: CardsConfigItem[] = [
      {
        component: <HomeAnnouncementsCard />,
        module: SiteModule.Announcements,
        key: 'announcements',
        fullWidth: true,
      },
    ];

    badgeRequestData?.data?.length &&
      cards.push({
        component: <HomeBadgesSection request={badgeRequestData} />,
        key: 'badges',
        fullWidth: true,
      });

    (isMemberplexTheme || isDemoTheme || isSmacnaTheme) &&
      cards.splice(2, 0, {
        component: (
          <Grid {...defaultGridContainerProps}>
            {enabledModules.includes(SiteModule.PersonalPayments) && (
              <Grid {...defaultGridItemProps} lg={6}>
                {!ordersRequestData.error ? <HomeOpenOrdersSection {...ordersRequestData} /> : null}
              </Grid>
            )}
            <Grid {...defaultGridItemProps} lg={6}>
              <HomeCreditsSection />
            </Grid>
          </Grid>
        ),
        key: 'tiles',
        fullWidth: enabledModules.includes(SiteModule.PersonalPayments),
      });

    (isMemberplexTheme || isDemoTheme) &&
      cards.splice(3, 0, {
        component: <HomeOrdersCard {...ordersRequestData} />,
        module: SiteModule.PersonalPayments,
        key: 'orders',
      });

    enableNavigationSection &&
      cards.unshift({
        component: <Grid {...defaultGridContainerProps}>{navigationCards}</Grid>,
        key: 'navigation',
        fullWidth: true,
      });

    enableNotes &&
      cards.splice(
        1,
        0,

        {
          component: <HomeNccerNoticeCard />,
          module: SiteModule.Notice,
          key: 'notice',
          fullWidth: true,
        }
      );

    !(isSmacnaTheme || isWcaTheme || isUsAseanTheme || isBayViewTheme) &&
      cards.splice(
        1,
        0,

        {
          component: <HomeMembershipCard />,
          module: SiteModule.Membership,
          key: 'membership',
        }
      );

    return cards;
  }, [
    badgeRequestData,
    isMemberplexTheme,
    isDemoTheme,
    isSmacnaTheme,
    enabledModules,
    ordersRequestData,
    enableNavigationSection,
    navigationCards,
    enableNotes,
    isWcaTheme,
    isUsAseanTheme,
    isBayViewTheme,
  ]);

  const content = useMemo(() => {
    const fullWidthContent: React.ReactNode[] = [];
    const cardContent: React.ReactNode[] = [];
    const cardsMap = new Map(cardsConfig.map((c) => [c.key, c]));

    const announcementsCard = cardsMap.get('announcements');
    const badgesCard = cardsMap.get('badges');

    const announcementsEnabled =
      !!announcementsCard && (!announcementsCard.module || enabledModules.includes(announcementsCard.module));
    const showSideBySide = announcementsEnabled && !!badgesCard;

    cardsConfig.forEach(({ fullWidth = false, component, module, key }) => {
      const isModuleEnabled = !module || enabledModules.includes(module);
      if (!component || !isModuleEnabled) return;

      if (showSideBySide && (key === 'announcements' || key === 'badges')) {
        if (key === 'badges') {
          fullWidthContent.push(
            <Grid {...defaultGridItemProps} key="badges-announcements" lg={12}>
              <Grid {...defaultGridContainerProps}>
                <Grid {...defaultGridItemProps} lg={6}>
                  <HomeBadgesSection request={badgeRequestData} fullWidth={true} />
                </Grid>
                <Grid {...defaultGridItemProps} lg={6}>
                  {announcementsCard.component}
                </Grid>
              </Grid>
            </Grid>
          );
        }

        return;
      }

      const node = (
        <Grid {...defaultGridItemProps} key={key} lg={fullWidth ? 12 : 6}>
          {component}
        </Grid>
      );

      if (fullWidth) fullWidthContent.push(node);
      else cardContent.push(node);
    });

    return (
      <>
        {fullWidthContent}
        {cardContent}
      </>
    );
  }, [cardsConfig, enabledModules, badgeRequestData]);

  const handleTermsAndConditionsModalOpen = useCallback(() => {
    setTermsAndConditionsModalOpen(true);
  }, []);

  const handleTermsAndConditionsModalClose = useCallback(() => {
    setTermsAndConditionsModalOpen(false);
  }, []);

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        <Grid {...defaultGridItemProps}>
          <Grid {...defaultGridContainerProps} alignItems={'baseline'} justifyContent={'space-between'}>
            <Grid {...defaultGridItemProps} sm={true}>
              <nav className={styles.quickLinks}>
                <span className={styles.label}>{'Quick Links:'}</span>
                {quickLinksConfig.map(
                  ({ label, link, tooltip = '', memberTypes = [] }, i) =>
                    hasMemberType(memberTypeName, memberTypes) && (
                      <Tooltip title={tooltip} arrow={true} key={`quick-link-${i}`} placement={'top'}>
                        <Button
                          variant={'contained'}
                          color={'primary'}
                          href={link}
                          target={'_blank'}
                          className={styles.quickLinkButton}
                        >
                          {label}
                        </Button>
                      </Tooltip>
                    )
                )}
              </nav>
            </Grid>
            {isCompassTheme && (
              <Grid {...defaultGridItemProps} sm={'auto'}>
                <Button variant={'contained'} color={'primary'} onClick={handleTermsAndConditionsModalOpen}>
                  {'Review Terms&Conditions'}
                </Button>
              </Grid>
            )}
            {isNccerTheme && enableCardNumber && (
              <Grid {...defaultGridItemProps} lg={'auto'} className={styles.cardNumberInfo}>
                {nccerCardNumber ? (
                  <span className={styles.label}>{`NCCER Card Number: ${nccerCardNumber}`}</span>
                ) : (
                  addNccerCardNumberUrl && (
                    <Button
                      variant={'outlined'}
                      color={'secondary'}
                      type={'submit'}
                      href={addNccerCardNumberUrl + encodeURIComponent(id)}
                    >
                      {'Add NCCER Card Number'}
                    </Button>
                  )
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        {content}
      </Grid>
      {isCompassTheme && (
        <DonationTermsModal open={termsAndConditionsModalOpen} onClose={handleTermsAndConditionsModalClose} />
      )}
    </>
  );
};
export default HomePage;
